/* eslint-disable @typescript-eslint/naming-convention */
// deps
import React, { memo, useCallback, useContext, useMemo } from "react";
import { useEffectOnce } from "@swiggy-private/core-ui";

// internal-deps
import { IBanner, DeliverySpeedDetail } from "@swiggy-private/cart-types";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { DELIVERY_SPEED_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { AnalyticsContext, AnalyticsContextValue } from "@swiggy-private/checkout-core";

// styles
import { BannerWrapper } from "./styles";

// icons
import { BannerIcon } from "./icons";
import { getShowBanner } from "./helpers";
import { FlowType } from "@swiggy-private/super-ui";

// dls
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";

// these are safety-net hexcodes, the colors would actually come from backend
const DEFAULT_COLOR = "#02060C99";
const DEFAULT_BACKGROUND_COLOR = "#F7F7F7";

interface BannerProps {
    bannerData?: IBanner;
    slaData?: DeliverySpeedDetail;
    setShowM2ChalfcardOnClick?: React.Dispatch<React.SetStateAction<{ status: boolean; flowType: FlowType }>>;
    impressionExpressBuyBlckNudge?: (impressionEvent: AnalyticsContextValue["impressionEvent"], context: string) => void;
    clickExpressBuyBlckNudge?: (clickEvent: AnalyticsContextValue["clickEvent"], context: string) => void;
    headingInfo?: string;
}

function Banner({
    bannerData,
    slaData,
    setShowM2ChalfcardOnClick,
    impressionExpressBuyBlckNudge,
    clickExpressBuyBlckNudge,
    headingInfo,
}: BannerProps) {
    const { text, color, bgColor, iconId, ctaEnabled } = bannerData || {};
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);

    const showBanner = getShowBanner(slaData, text);

    const analyticsContext = useMemo(
        () =>
            JSON.stringify({
                text: String(text),
                heading_info: headingInfo,
                eta_upper_psla: slaData?.sla_range_max,
                eta_lower_psla: slaData?.sla_range_min,
                sla: slaData?.sla,
                delivery_time: slaData?.o2r?.point,
            }),
        [headingInfo, slaData?.o2r?.point, slaData?.sla, slaData?.sla_range_max, slaData?.sla_range_min, text],
    );

    useEffectOnce(() => {
        impressionExpressBuyBlckNudge?.(impressionEvent, analyticsContext);
    });

    const onBannerClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            if (ctaEnabled && setShowM2ChalfcardOnClick) {
                setShowM2ChalfcardOnClick({ status: true, flowType: FlowType.EXPRESS_HALF_CARD });
                clickExpressBuyBlckNudge?.(clickEvent, analyticsContext);
            }
        },
        [ctaEnabled, setShowM2ChalfcardOnClick, clickExpressBuyBlckNudge, analyticsContext, clickEvent],
    );

    if (!showBanner) {
        return null;
    }

    const textColor = color || DEFAULT_COLOR;
    const backgroundColor = bgColor || DEFAULT_BACKGROUND_COLOR;

    return (
        <BannerWrapper
            bgColor={backgroundColor}
            data-testid={DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_EXPRESS_BANNER}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => onBannerClick(e)}
        >
            <BannerIcon iconId={iconId} />
            <Text
                variant="V2.body3.neutral.medium"
                customColor={{ light: textColor, dark: textColor }}
                data-testid={DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_EXPRESS_BANNER_TEXT}
            >
                {text}
            </Text>
            {ctaEnabled && (
                <SpriteIconWeb
                    iconName="chevronRight12"
                    strokeColor="secondary_One_Blck"
                    fillColor="secondary_One_Blck"
                ></SpriteIconWeb>
            )}
        </BannerWrapper>
    );
}

export default memo(Banner);
